import React, { useContext, useState } from "react";
import { AuthUserContext } from "../../context/AuthUserContext";
import CheckoutAddresses from "../popup/CheckoutAddresses";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardTitle,
  Divider,
  FieldGrid,
  FullWidthField,
  FullWidthSplitField,
  Field,
  Label,
  Input,
  InputSelect,
  PrimaryButton,
  ButtonRow,
} from "./CheckoutUI";

const Step1Exists = ({ onNext }) => {
  const { t } = useTranslation();
  const { userProfile, updateAddressBook, createAddressBook, updateUserInfo } =
    useContext(AuthUserContext);
  const [showConfirm, setShowConfirm] = useState(null);

  const primaryAddress =
    userProfile.addresses?.find((address) => address.is_primary) || {};

  const [data, setData] = useState({
    user: {
      email: userProfile?.email || "",
      firstName: userProfile?.first_name || "",
      lastName: userProfile?.last_name || "",
    },
    address: {
      country: primaryAddress?.country || "",
      city: primaryAddress?.city || "",
      postal_code: primaryAddress?.postal_code || "",
      street: primaryAddress?.street || "",
      street_number: primaryAddress?.street_number || "",
      secondary_street: primaryAddress?.secondary_street || "",
      building_number: primaryAddress?.building_number || "",
      latitude: primaryAddress?.latitude || null,
      longitude: primaryAddress?.longitude || null,
      phone_number:
        primaryAddress?.phone_number || userProfile?.phone_number || "",
      is_primary: true,
    },
  });

  const handleChange = (e, section) => {
    const { name, value, type, checked } = e.target;

    setData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      if (
        data.user.firstName !== userProfile.first_name ||
        data.user.lastName !== userProfile.last_name
      ) {
        await updateUserInfo({
          first_name: data.user.firstName,
          last_name: data.user.lastName,
        });
      }

      let updatedOrCreatedAddress;

      // Provera da li su podaci o adresi promenjeni
      const isAddressUnchanged =
        primaryAddress?.id &&
        primaryAddress.country === data.address.country &&
        primaryAddress.city === data.address.city &&
        primaryAddress.postal_code === data.address.postal_code &&
        primaryAddress.street === data.address.street &&
        primaryAddress.street_number === data.address.street_number &&
        primaryAddress.secondary_street === data.address.secondary_street &&
        primaryAddress.building_number === data.address.building_number &&
        primaryAddress.phone_number === data.address.phone_number;

      if (isAddressUnchanged) {
        // Ako se podaci nisu promenili, preskoči API poziv
        console.log("Address unchanged. No API call needed.");
        updatedOrCreatedAddress = primaryAddress;
      } else if (primaryAddress?.id) {
        // Ako postoji adresa, ažuriraj je
        updatedOrCreatedAddress = await updateAddressBook(
          primaryAddress.id,
          data.address
        );
        console.log("Address updated successfully.");
      } else {
        // Ako adresa ne postoji, kreiraj je
        updatedOrCreatedAddress = await createAddressBook(data.address);
        console.log("Address created successfully.");
      }

      // Prosledi ID adrese na `onNext`
      if (userProfile.addresses && userProfile.addresses.length > 1) {
        setShowConfirm(updatedOrCreatedAddress);
      } else {
        onNext(updatedOrCreatedAddress);
      }
    } catch (error) {
      console.error("Error handling address:", error);
      alert("There was an error processing your address. Please try again.");
    }
  };

  return (
    <>
      {showConfirm && (
        <CheckoutAddresses
          chosenAddress={showConfirm}
          setShowConfirm={setShowConfirm}
          onNext={onNext}
        />
      )}

      <Card>
        <CardTitle>{t("CHECKOUT.CONTACT_DETAILS", "Contact details")}</CardTitle>
        <FieldGrid>
          <Field>
            <Label htmlFor="firstName">{t("LABELS.NAME")} *</Label>
            <Input
              type="text"
              id="firstName"
              name="firstName"
              value={data.user.firstName}
              onChange={(e) => handleChange(e, "user")}
              placeholder={t("PLACEHOLDERS.NAME")}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="lastName">{t("LABELS.SURNAME")} *</Label>
            <Input
              type="text"
              id="lastName"
              name="lastName"
              value={data.user.lastName}
              onChange={(e) => handleChange(e, "user")}
              placeholder={t("PLACEHOLDERS.SURNAME")}
              required
            />
          </Field>
          <FullWidthField>
            <Field>
              <Label htmlFor="phone_number">{t("LABELS.PHONE_NUMBER")} *</Label>
              <Input
                type="text"
                id="phone_number"
                name="phone_number"
                value={data.address.phone_number}
                onChange={(e) => handleChange(e, "address")}
                placeholder={t("PLACEHOLDERS.PHONE_NUMBER")}
                required
              />
            </Field>
          </FullWidthField>
        </FieldGrid>

        <Divider />

        <CardTitle>{t("CHECKOUT.DELIVERY_ADDRESS", "Delivery address")}</CardTitle>
        <FieldGrid>
          <FullWidthField>
            <Field>
              <Label htmlFor="country">{t("LABELS.COUNTRY")} *</Label>
              <InputSelect
                id="country"
                name="country"
                value={data.address.country}
                onChange={(e) => handleChange(e, "address")}
                required
              >
                <option value="">{t("PLACEHOLDERS.COUNTRY")}</option>

                <option value="AT">{t("COUNTRIES.AT")}</option>
                <option value="DE">{t("COUNTRIES.DE")}</option>
                <option value="CH">{t("COUNTRIES.CH")}</option>
                <option value="FR">{t("COUNTRIES.FR")}</option>
                <option value="IT">{t("COUNTRIES.IT")}</option>
                <option value="ES">{t("COUNTRIES.ES")}</option>
                <option value="NL">{t("COUNTRIES.NL")}</option>
                <option value="BE">{t("COUNTRIES.BE")}</option>
                <option value="SE">{t("COUNTRIES.SE")}</option>
                <option value="NO">{t("COUNTRIES.NO")}</option>
                <option value="DK">{t("COUNTRIES.DK")}</option>
                <option value="FI">{t("COUNTRIES.FI")}</option>
                <option value="PL">{t("COUNTRIES.PL")}</option>
                <option value="CZ">{t("COUNTRIES.CZ")}</option>
                <option value="SK">{t("COUNTRIES.SK")}</option>
                <option value="HU">{t("COUNTRIES.HU")}</option>
                <option value="RO">{t("COUNTRIES.RO")}</option>
                <option value="BG">{t("COUNTRIES.BG")}</option>
                <option value="HR">{t("COUNTRIES.HR")}</option>
                <option value="SI">{t("COUNTRIES.SI")}</option>
                <option value="RS">{t("COUNTRIES.RS")}</option>
              </InputSelect>
            </Field>
          </FullWidthField>

          <Field>
            <Label htmlFor="postal_code">{t("LABELS.POSTAL_CODE")} *</Label>
            <Input
              type="text"
              id="postal_code"
              name="postal_code"
              value={data.address.postal_code}
              onChange={(e) => handleChange(e, "address")}
              placeholder={t("PLACEHOLDERS.POSTAL_CODE")}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="city">{t("LABELS.CITY")} *</Label>
            <Input
              type="text"
              id="city"
              name="city"
              value={data.address.city}
              onChange={(e) => handleChange(e, "address")}
              placeholder={t("PLACEHOLDERS.CITY")}
              required
            />
          </Field>

          <FullWidthSplitField>
            <Field>
              <Label htmlFor="street">{t("LABELS.STREET")} *</Label>
              <Input
                type="text"
                id="street"
                name="street"
                value={data.address.street}
                onChange={(e) => handleChange(e, "address")}
                placeholder={t("PLACEHOLDERS.STREET")}
                required
              />
            </Field>
            <Field>
              <Label htmlFor="street_number">{t("LABELS.STREET_NUMBER")}</Label>
              <Input
                type="text"
                id="street_number"
                name="street_number"
                value={data.address.street_number}
                onChange={(e) => handleChange(e, "address")}
                placeholder={t("PLACEHOLDERS.STREET_NUMBER")}
              />
            </Field>
          </FullWidthSplitField>

          <Field>
            <Label htmlFor="secondary_street">
              {t("LABELS.SECONDARY_STREET")}
            </Label>
            <Input
              type="text"
              id="secondary_street"
              name="secondary_street"
              value={data.address.secondary_street}
              onChange={(e) => handleChange(e, "address")}
              placeholder={t("PLACEHOLDERS.SECONDARY_STREET")}
            />
          </Field>
          <Field>
            <Label htmlFor="building_number">
              {t("LABELS.BUILDING_NUMBER")}
            </Label>
            <Input
              type="text"
              id="building_number"
              name="building_number"
              value={data.address.building_number}
              onChange={(e) => handleChange(e, "address")}
              placeholder={t("PLACEHOLDERS.BUILDING_NUMBER")}
            />
          </Field>
        </FieldGrid>

        <ButtonRow>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={
              !data.user.firstName ||
              !data.user.lastName ||
              !data.address.phone_number ||
              !data.address.street ||
              !data.address.postal_code ||
              !data.address.city ||
              !data.address.country
            }
          >
            {t("BUTTONS.CONTINUE")}
          </PrimaryButton>
        </ButtonRow>
      </Card>
    </>
  );
};

export default Step1Exists;
